import { Box, Chip, Stack, Typography } from '@mui/material';
import { CheckCircleOutline, InfoOutlined, RemoveCircleOutline } from '@mui/icons-material';
import { text } from '../../theme/colors';

const INK = '#1e293b';
const AMBER_BG = '#fdf2e9';
const AMBER = '#9a3412';
const TEAL = '#134e4a';

// What a cession is and what Eqaya checks on one, in the owner's words
// (2026-09-26). The same text goes on every cession listing and on the
// "How buying works" page, so buyers get one explanation wherever they meet it.
export const CESSION_CHECKS = [
  ['The seller holds the rights', 'A letter from the council or developer confirms the seller is the current rights holder on their register. Our verifier confirms this at that office, in place of the Deeds Office search.'],
  ['The chain of paper', 'The original agreement of sale or lease with the council or developer, plus any earlier cessions.'],
  ['The developer is entitled to sell', 'The developer’s title deed for the parent land, and the approved subdivision permit and layout plan.'],
  ['Survey and servicing', 'Whether the stand is surveyed with a diagram and beacons, and whether roads, water and sewer are in.'],
  ['Dues and fees', 'Payments to the council or developer are up to date, and it is clear who pays the cession fee.'],
];

const yesNo = (v, yes, no) => (v === true ? yes : v === false ? no : null);
const ISSUER = { local_authority: 'a council', developer: 'a private land developer' };
const FEE = { buyer: 'the buyer', seller: 'the seller', shared: 'buyer and seller, shared' };

/**
 * How a cession works and what Eqaya checks. `facts` is the listing's verified
 * cession summary from the API (optional): when present, the page also says
 * what was found for this stand.
 */
export default function CessionExplainer({ facts = null, compact = false }) {
  const found = facts?.verified ? [
    facts.issuer_type && ['Rights granted by', `${ISSUER[facts.issuer_type] || facts.issuer_type}${facts.issuer_name ? ` (${facts.issuer_name})` : ''}`],
    facts.prior_cessions != null && ['Earlier cessions', facts.prior_cessions === 0 ? 'None: the seller is the first holder' : String(facts.prior_cessions)],
    yesNo(facts.surveyed, 'Surveyed, with a diagram and beacons', 'Not surveyed yet') && ['Survey', yesNo(facts.surveyed, 'Surveyed, with a diagram and beacons', 'Not surveyed yet')],
    yesNo(facts.services_in, 'Roads, water and sewer in', 'Services not in yet') && ['Services', yesNo(facts.services_in, 'Roads, water and sewer in', 'Services not in yet')],
    yesNo(facts.dues_up_to_date, 'Up to date', 'Not up to date') && ['Payments to the council or developer', yesNo(facts.dues_up_to_date, 'Up to date', 'Not up to date')],
    facts.cession_fee_payer && ['Cession fee paid by', FEE[facts.cession_fee_payer] || facts.cession_fee_payer],
  ].filter(Boolean) : [];

  return (
    <Box component="section" aria-label="This property is a cession" sx={{ borderRadius: 2, boxShadow: '0 0 0 1px #f3d9c4', bgcolor: '#fff', overflow: 'hidden' }}>
      <Box sx={{ bgcolor: AMBER_BG, px: { xs: 2.25, md: 3 }, py: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip label="Cession" size="small" sx={{ bgcolor: AMBER, color: '#fff', fontWeight: 700, borderRadius: '6px' }} />
          <Typography component="h2" sx={{ fontWeight: 800, color: INK, fontSize: { xs: 18, md: 20 } }}>No title deed yet: this is a cession</Typography>
        </Stack>
        <Typography sx={{ mt: 1, fontSize: 15, lineHeight: 1.6, color: INK, maxWidth: 780 }}>
          With a cession, the seller holds rights to the stand, not a registered title. Those rights usually come from an agreement with the council or a private land developer. The sale is done by ceding those rights, recorded at the council&apos;s or developer&apos;s office, not at the Deeds Office.
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 15, lineHeight: 1.6, color: AMBER, fontWeight: 600, maxWidth: 780 }}>
          This is where most double-selling and land-baron fraud happens, so the checks are different, not skipped.
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
        <Typography sx={{ fontWeight: 700, color: INK, mb: 1.5 }}>What Eqaya checks on a cession</Typography>
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: compact ? '1fr' : '1fr 1fr' }, gap: 1.75 }}>
          {CESSION_CHECKS.map(([title, body], i) => (
            <Box component="li" key={title} sx={{ display: 'flex', gap: 1.5 }}>
              <Box aria-hidden sx={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', bgcolor: TEAL, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{i + 1}</Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: INK, fontSize: 15 }}>{title}</Typography>
                <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: text.secondary }}>{body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {found.length > 0 && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #eef2f1' }}>
            <Typography sx={{ fontWeight: 700, color: INK, mb: 1 }}>What we found for this stand</Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' }, columnGap: 3, rowGap: 0.75 }}>
              {found.map(([k, v]) => {
                const bad = /^Not |not in yet/.test(v);
                return (
                  <Box key={k} sx={{ display: 'contents' }}>
                    <Typography component="dt" sx={{ fontSize: 14, color: text.secondary }}>{k}</Typography>
                    <Typography component="dd" sx={{ m: 0, fontSize: 14.5, fontWeight: 600, color: bad ? AMBER : INK, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {bad ? <RemoveCircleOutline sx={{ fontSize: 17 }} /> : <CheckCircleOutline sx={{ fontSize: 17, color: TEAL }} />}{v}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2.25, p: 1.75, borderRadius: '10px', bgcolor: '#f6f9f8' }}>
          <InfoOutlined sx={{ fontSize: 19, color: TEAL, mt: '1px' }} />
          <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: INK }}>
            Before any money moves, have your own conveyancer confirm the cession with the council or developer, and make sure the cession to you is recorded there.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}
